import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import api, { fileToBase64 } from "@/lib/api";
import { CATEGORIES } from "@/pages/Carte";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const emptyItem = {
  category: "entrees",
  name_fr: "",
  name_en: "",
  description_fr: "",
  description_en: "",
  price: "",
  photo: "",
  photos: [],
  order: 0,
  published: true,
  takeaway: true,
  allergens: "",
  spice_level: 0,
  supplements: "",
  prep_time: "",
  pairings: "",
};

export default function MenuManager() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [editingCat, setEditingCat] = useState(null);

  const load = () => api.get("/admin/menu").then((r) => setItems(r.data)).catch(() => {});
  const loadCats = () =>
    api
      .get("/categories")
      .then((r) => {
        if (r.data.length) setCategories(r.data);
      })
      .catch(() => {});
  useEffect(() => {
    load();
    loadCats();
  }, []);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/admin/categories", { label: newCat.trim(), order: categories.length });
      setNewCat("");
      loadCats();
      toast.success("Catégorie ajoutée.");
    } catch {
      toast.error("Cette catégorie existe déjà.");
    }
  };

  const renameCategory = async () => {
    await api.put(`/admin/categories/${editingCat.id}`, {
      label: editingCat.label,
      order: editingCat.order,
    });
    setEditingCat(null);
    loadCats();
    toast.success("Catégorie renommée.");
  };

  const deleteCategory = async (id) => {
    await api.delete(`/admin/categories/${id}`);
    loadCats();
    toast.success("Catégorie supprimée.");
  };

  const openNew = () => setEditing({ ...emptyItem });
  const openEdit = (item) => setEditing({ ...item });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/menu/${editing.id}`, editing);
      } else {
        await api.post("/admin/menu", editing);
      }
      toast.success("Plat enregistré.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/menu/${id}`);
    toast.success("Plat supprimé.");
    load();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setEditing((s) => ({ ...s, photo: base64 }));
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  return (
    <div data-testid="menu-manager">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-ivory">Carte — {items.length} élément(s)</h2>
        <button onClick={openNew} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="menu-add-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter un plat
        </button>
      </div>

      <div className="mb-10 rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="category-manager">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">Catégories</h3>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <span
              key={c.id}
              className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-xs text-ivory/70"
              data-testid={`category-chip-${c.id}`}
            >
              {editingCat?.id === c.id ? (
                <>
                  <input
                    value={editingCat.label}
                    onChange={(e) => setEditingCat({ ...editingCat, label: e.target.value })}
                    className="w-32 bg-transparent outline-none"
                    data-testid={`category-rename-input-${c.id}`}
                  />
                  <button onClick={renameCategory} aria-label="Valider" className="text-gold" data-testid={`category-rename-save-${c.id}`}>
                    <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </>
              ) : (
                <>
                  {c.label}
                  <button onClick={() => setEditingCat(c)} aria-label="Renommer" className="text-ivory/40 transition-colors hover:text-gold" data-testid={`category-edit-${c.id}`}>
                    <Pencil className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => deleteCategory(c.id)} aria-label="Supprimer" className="text-ivory/40 transition-colors hover:text-red-400" data-testid={`category-delete-${c.id}`}>
                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </>
              )}
            </span>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <input
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            placeholder="Nouvelle catégorie (ex : Bières)"
            className="input-dark !h-10 max-w-xs"
            data-testid="category-new-input"
          />
          <button onClick={addCategory} className="btn-outline !h-10 !px-4 !text-[11px]" data-testid="category-add-button">
            Ajouter
          </button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="menu-edit-modal">
          <form
            onSubmit={save}
            className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier le plat" : "Nouveau plat"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer" data-testid="menu-edit-close">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Catégorie</label>
                <Select value={editing.category} onValueChange={(v) => setEditing((s) => ({ ...s, category: v }))}>
                  <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="menu-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-coal text-ivory">
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Prix</label>
                <input value={editing.price} onChange={set("price")} placeholder="Ex : 24 €" className="input-dark !h-11" data-testid="menu-price-input" />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Nom (français)</label>
                <input required value={editing.name_fr} onChange={set("name_fr")} className="input-dark !h-11" data-testid="menu-name-fr-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Nom (anglais)</label>
                <input value={editing.name_en} onChange={set("name_en")} className="input-dark !h-11" data-testid="menu-name-en-input" />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Description (français)</label>
              <textarea rows={2} value={editing.description_fr} onChange={set("description_fr")} className="input-dark !h-auto py-3" data-testid="menu-desc-fr-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Description (anglais)</label>
              <textarea rows={2} value={editing.description_en} onChange={set("description_en")} className="input-dark !h-auto py-3" data-testid="menu-desc-en-input" />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Photo principale (facultative)</label>
                {editing.photo && (
                  <img src={editing.photo} alt="" className="mb-3 h-20 w-20 rounded-md border border-white/10 object-cover" />
                )}
                <input type="file" accept="image/*" onChange={uploadPhoto} className="text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:text-ivory" data-testid="menu-photo-upload" />
              </div>
              <div className="flex items-end justify-between gap-4 pb-1">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Ordre</label>
                  <input type="number" value={editing.order} onChange={set("order")} className="input-dark !h-11 w-24" data-testid="menu-order-input" />
                </div>
                <label className="flex items-center gap-3 text-sm text-ivory/70">
                  Publié
                  <Switch
                    checked={editing.published}
                    onCheckedChange={(v) => setEditing((s) => ({ ...s, published: v }))}
                    data-testid="menu-published-switch"
                  />
                </label>
                <label className="flex items-center gap-3 text-sm text-ivory/70">
                  À emporter
                  <Switch
                    checked={editing.takeaway !== false}
                    onCheckedChange={(v) => setEditing((s) => ({ ...s, takeaway: v }))}
                    data-testid="menu-takeaway-switch"
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                Photos supplémentaires (carrousel)
              </label>
              <div className="flex flex-wrap items-center gap-3">
                {(editing.photos || []).map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="h-16 w-16 rounded-md border border-white/10 object-cover" />
                    <button
                      type="button"
                      onClick={() => setEditing((s) => ({ ...s, photos: s.photos.filter((_, j) => j !== i) }))}
                      aria-label="Retirer"
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white"
                      data-testid={`menu-photo-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-white/20 text-ivory/40 transition-colors hover:border-copper hover:text-copper" data-testid="menu-photos-upload">
                  +
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const b64 = await fileToBase64(file);
                      setEditing((s) => ({ ...s, photos: [...(s.photos || []), b64] }));
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Allergènes</label>
                <input value={editing.allergens || ""} onChange={set("allergens")} placeholder="Ex : arachides, gluten" className="input-dark !h-11" data-testid="menu-allergens-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                  Niveau de piment — {editing.spice_level || 0}/3
                </label>
                <input type="range" min="0" max="3" step="1" value={editing.spice_level || 0} onChange={(e) => setEditing((s) => ({ ...s, spice_level: parseInt(e.target.value, 10) }))} className="mt-3 w-full accent-[#C4704F]" data-testid="menu-spice-input" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Temps de préparation</label>
                <input value={editing.prep_time || ""} onChange={set("prep_time")} placeholder="Ex : 20 min" className="input-dark !h-11" data-testid="menu-preptime-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Suppléments</label>
                <input value={editing.supplements || ""} onChange={set("supplements")} placeholder="Ex : alloco +3 €" className="input-dark !h-11" data-testid="menu-supplements-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Accompagnements suggérés</label>
                <input value={editing.pairings || ""} onChange={set("pairings")} placeholder="Ex : riz parfumé, alloco" className="input-dark !h-11" data-testid="menu-pairings-input" />
              </div>
            </div>

            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="menu-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="menu-empty">
          Aucun plat pour le moment. Ajoutez votre premier plat.
        </p>
      ) : (
        <div className="space-y-2" data-testid="menu-items-list">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5"
              data-testid={`menu-row-${item.id}`}
            >
              <span className="w-40 shrink-0 text-xs uppercase tracking-wider text-gold/70">
                {categories.find((c) => c.id === item.category)?.label || item.category}
              </span>
              <span className={`flex-1 truncate text-sm ${item.published ? "text-ivory" : "text-ivory/35 line-through"}`}>
                {item.name_fr}
                {item.takeaway === false && (
                  <span className="ml-2 rounded border border-white/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ivory/40">
                    sur place uniquement
                  </span>
                )}
              </span>
              <span className="text-sm text-gold">{item.price}</span>
              <button onClick={() => openEdit(item)} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`menu-edit-${item.id}`}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
              <button onClick={() => remove(item.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`menu-delete-${item.id}`}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
